<template>
    <div
        :class="['stat-item', { 'no-stats': !hasSentCount, disabled }]"
    >
        <section class="stat-description" @click="handleItemClick">
            <small class="sms-broadcast-label">
                {{ $options.TEMPLATE_CONSTANTS.EMAIL }}
            </small>

            <span
                class="stat-subject"
                data-qa="email-broadcast-subject"
                :title="broadcast.subject"
            >
                {{ broadcastSubject }}
            </span>

            <div class="stat-message" data-qa="email-broadcast-date-message">
                <span>{{ broadcastDateMessage }}</span>
            </div>
        </section>

        <section class="stat-status" @click="handleItemClick">
            <ds-chip
                :class="['chip', `${broadcast.status}`]"
            >
                {{ broadcastStatusMessage }}
            </ds-chip>
        </section>

        <section class="stat-stats" @click="handleItemClick">
            <div v-if="showOpens" class="stat-block" data-qa="opens-percentage">
                <span class="table-number percentage">{{ opensPercentage }}</span>

                <span v-if="isDoneBroadcastStatus" class="percentage-label">{{ $options.TEMPLATE_CONSTANTS.OPENS }}</span>
            </div>
            <div class="stat-block clicks-percentage">
                <span class="table-number percentage">{{ clicksPercentage }}</span>

                <span v-if="isDoneBroadcastStatus" class="percentage-label">{{ $options.TEMPLATE_CONSTANTS.CLICKS }}</span>
            </div>
        </section>

        <section v-if="!disabled" ref="options" class="stat-options">
            <email-broadcast-options
                :status="broadcast.status"
                :schedule-date-without-time-zone="broadcast.scheduleDateWithoutTimeZone"
                :use-contact-time-zone="broadcast.useContactTimeZone"
                :scheduled-time-zone-id="broadcast.scheduledTimeZoneId"
                :editor="broadcast.editor"
                @edit="editBroadcast"
                @copy="copyBroadcast"
                @report="viewBroadcast"
                @reschedule="openRescheduleDialog"
                @cancel="cancelBroadcast"
                @resume="resumeBroadcast"
                @delete="openDeleteModal"
            />
        </section>

        <DsConfirmDialog
            destructive
            :is-open="confirmingDelete"
            :title="$options.TEMPLATE_CONSTANTS.DELETE_MODAL_TITLE"
            :message="$options.TEMPLATE_CONSTANTS.DELETE_MODAL_MESSAGE"
            :confirm-label="$options.TEMPLATE_CONSTANTS.BUTTON"
            @cancel="closeDeleteModal"
            @confirm="deleteBroadcast"
        />
    </div>
</template>

<script>
import EmailBroadcastOptions from './EmailBroadcastOptions';
import {
    BROADCAST_STATUS,
    BROADCAST_STATUS_MESSAGE,
} from '../constants/emailBroadcast.constants';
import { broadcastDateMessage } from '@/components/broadcast/utils/date.util';

export const TEMPLATE_CONSTANTS = {
    EMAIL: 'Email',
    OPENS: 'Opens',
    CLICKS: 'Clicks',
    DELETE_MODAL_TITLE: 'Really Delete?',
    DELETE_MODAL_MESSAGE: 'Do you really want to delete this draft?',
    BUTTON: 'Ok',
};

export default {
    name: 'EmailBroadcastItem',

    TEMPLATE_CONSTANTS,

    components: {
        EmailBroadcastOptions,
    },

    props: {
        broadcast: {
            type: Object,
            required: true,
        },
        isScheduledEmailDateTimeFixEnabled: Boolean,
        showOpens: Boolean,
    },

    data() {
        return {
            confirmingDelete: false,
            scrollY: 0,
        };
    },

    computed: {
        broadcastDateMessage() {
            return broadcastDateMessage(this.broadcast, this.isScheduledEmailDateTimeFixEnabled);
        },

        broadcastStatusMessage() {
            let { status } = this.broadcast;

            if (status == null) {
                status = 'Draft';
            }

            status = BROADCAST_STATUS_MESSAGE[status];

            return status;
        },

        broadcastSubject() {
            return this.broadcast.subject || '(no subject)';
        },

        clicksPercentage() {
            let response = '-';

            if (this.shouldShowClickAndSentStats()) {
                const { clickPercent } = this.broadcast;

                response = `${clickPercent}%`;
            }

            return response;
        },

        disabled() {
            return (this.broadcast.mailBatchId === 0 || this.confirmingDelete) && this.broadcast.status !== BROADCAST_STATUS.DRAFT;
        },

        opensPercentage() {
            let response = '-';

            if (this.shouldShowClickAndSentStats()) {
                const { openPercent } = this.broadcast;

                response = `${openPercent}%`;
            }

            return response;
        },

        hasSentCount() {
            const { sentCount } = this.broadcast;

            return sentCount != null && sentCount > 0;
        },

        isDoneBroadcastStatus() {
            return this.broadcast.status && this.broadcast.status === BROADCAST_STATUS.DONE;
        },
    },

    methods: {
        cancelBroadcast() {
            this.$emit('cancel', this.broadcast.mailBatchId);
        },

        copyBroadcast() {
            this.$emit('copy-broadcast', this.broadcast.id);
        },

        deleteBroadcast() {
            this.closeDeleteModal();
            this.$emit('delete-draft', this.broadcast.id);
        },

        openRescheduleDialog() {
            this.$emit('open-reschedule-modal');
        },

        openDeleteModal() {
            this.scrollY = window.scrollY;

            this.confirmingDelete = true;

            // to preserve scroll position
            this.$nextTick(() => {
                document.body.scrollTo({
                    left: 0,
                    top: this.scrollY,
                    behavior: 'instant',
                });
            });
        },

        closeDeleteModal() {
            this.confirmingDelete = false;

            this.$nextTick(() => {
                document.body.scrollTo({
                    left: 0,
                    top: this.scrollY,
                    behavior: 'instant',
                });
            });
        },

        resumeBroadcast() {
            this.$emit('resume', this.broadcast.mailBatchId);
        },

        viewBroadcast() {
            window.location.href = `/app/report/viewEmailBatchReport/${this.broadcast.mailBatchId}`;
        },

        editBroadcast() {
            this.$emit('edit-broadcast', this.broadcast.id);
        },

        handleItemClick() {
            if (this.broadcast.status === BROADCAST_STATUS.DRAFT) {
                this.editBroadcast();
            } else {
                this.viewBroadcast();
            }
        },

        shouldShowClickAndSentStats() {
            return this.broadcast.status === BROADCAST_STATUS.DONE;
        },
    },

};
</script>
<style>
    small.sms-broadcast-label {
        font-size: .75rem;
        color: rgba(0,0,0,.6);
    }

    .stat-item > .stat-options > .broadcast-options > .dropdown-menu > .menu > .dropdown-menu-list {
        padding: 0.25rem 0;
        margin: 10px 0px;
        color: black;
    }
</style>
<style lang="scss" rel="stylesheet/scss" scoped>
    .clicks-percentage {
        grid-column: 2 / 3
    }

    .stat-item {
        @include transition(background-color);
        cursor: pointer;
        padding: $spacing-100;
        grid-gap: $spacing-200;
        display: grid;
        grid-template-columns: 4fr 4fr 3fr 1fr;
        grid-template-areas:
                "description status stats options";
        border-bottom: 1px solid $color-gray-200;

        &:not(.disabled) {
            .stat-subject {
                cursor: pointer;
            }

            &:hover {
                background-color: $color-gray-050;
            }
        }

        &.disabled {
            opacity: 0.6;
        }

        @media($small) {
            padding: $spacing-200 0;
            grid-template-areas:
                    "description status options"
                    "stats . .";

            &.no-stats {
                grid-template-areas:
                        "description status options"
            }
        }
    }

    .stat-description {
        grid-area: description;
        display: flex;
        flex-direction: column;
        min-width: 0;
        max-width: 100%;
    }

    .stat-subject {
        @include ellipsis;
        font-size: $font-size-md;
        padding-bottom: $spacing-050;
    }

    .stat-placeholder {
        width: calc(100% - #{$spacing-200});
    }

    .stat-message {
        font-size: $font-size-sm;
        color: $color-gray-700;
        padding-bottom: $spacing-100;
        display: flex;
        align-items: center;
    }

    .stat-status {
        display: grid;
        grid-area: status;
        flex-direction: column;
        align-items: center;
        width: fit-content;
        margin-left: 50%;
        grid-template-areas: 'status splitBroadcast';
    }

    .table-number {
        margin-top: 20%;
    }

    div.chip.CANCELLED,
    div.chip.ERROR {
        background-color: $color-red-050;
    }

    div.chip.QUEUED,
    div.chip.PROCESSING {
        background-color: $color-orange-050;
    }

    div.chip.DONE {
        background-color: $color-green-050;
    }

    .stat-stats {
        grid-area: stats;
        display: grid;
        grid-template-columns: 1fr 1fr;
    }

    .stat-block {
        display: flex;
        flex-direction: column;

        .table-number {
            @include text-align-end;
        }

        .percentage-label {
            color: $color-gray-700;
            font-size: $font-size-xs;
            @include text-align-end;
        }
    }

    .stat-options {
        display: flex;
        justify-content: flex-end;
        align-items: baseline;
        justify-self: end;
        grid-area: options;
        margin-top: 18%;
    }

</style>
